import { useState, useEffect } from "react"
import { Handle, Node, NodeProps, Position } from "@xyflow/react"
import { Box } from "@chakra-ui/react"
import MonitoringActivationWorkflow from "@hydrologiq/shared/chakra/MonitoringActivationWorkflow"
import { AssetKind as GraphQLAssetKind } from "@hydrologiq/gql-experience/graphql"
import { IconType } from "react-icons"
import {
  FaTruckDroplet,
  FaBolt,
  FaDatabase,
  FaEye,
  FaDroplet,
  FaSolarPanel,
  FaPlug,
  FaCarBattery,
  FaCircleQuestion,
} from "react-icons/fa6"
import { AssetType } from "./Asset"
import { AssetInfo, LoadPrecedence, NodeAlertSummary, NodeType, OperationalStatus } from "../../types/SystemView"
import { RefObject } from "react"
import ApiAssetImplV1 from "./impl/ApiAssetV1"
import ApiAssetImplV2, { ApiAssetAssignment, ApiAssetAttributeProps } from "./impl/ApiAssetV2"
import { useSystemsMonitoringWorkflow } from "../../hooks/useSystemsMonitoringWorkflow"
import { SystemNodeVersion } from "@hydrologiq/systems/types/SystemNode"

// Extended asset data interface for API-driven nodes
interface ApiAssetData {
  label: string
  type: AssetType
  id: string
  assignmentStatus?: "active" | "inactive" | null
  assignment?: ApiAssetAssignment | null
  operationalStatus?: OperationalStatus
  asset?: AssetInfo | null
  installationId?: string
  systemId?: string
  nodeType?: NodeType
  icon?: IconType
  refetch?: () => void | Promise<void>
  checkGroupConflict?: (installationId: string, currentAssetId: string) => boolean
  pollingTimeoutMs?: number // Configurable timeout for polling (default: 4 minutes)
  pollingIntervalMs?: number // Configurable interval for polling (default: 20 seconds)
  [key: string]: unknown
  container?: RefObject<HTMLElement | null>
  disabled?: boolean
  attributes?: ApiAssetAttributeProps[]
  controllable?: boolean
  version?: SystemNodeVersion
  alerts?: NodeAlertSummary
  loadPrecedence?: LoadPrecedence
  connectedHandleIds?: Set<string>
}

type ApiAssetNodeProps = NodeProps<ApiAssetNode>

const noop = () => {}

const DEFAULT_ASSET_ICONS: Record<AssetType, IconType> = {
  [AssetType.Generator]: FaBolt,
  [AssetType.Consumer]: FaBolt,
  [AssetType.Storage]: FaDatabase,
  [AssetType.Producer]: FaTruckDroplet,
}

// Map node types to specific icons when available
const NODE_TYPE_ICONS: Partial<Record<NodeType, IconType>> = {
  monitoring_device: FaEye,
  hydrogen_producer: FaDroplet,
  storage_hub: FaDatabase,
  refueling_bowser: FaTruckDroplet,
  grid_connection: FaPlug,
  solar_generation: FaSolarPanel,
  battery: FaCarBattery,
  electrolyser: FaBolt,
}

type ApiAssetNode = Node<ApiAssetData, "apiAsset">

const ApiAssetNode = ({ data, isConnectable }: ApiAssetNodeProps) => {
  // Local state to track assignment status (starts with data value, can be updated independently)
  const initialAssignmentStatus =
    (data.assignment ? data.assignment.status : data.assignmentStatus) ||
    (!data.controllable && data.operationalStatus === "active" ? "active" : "inactive")
  const [localAssignmentStatus, setLocalAssignmentStatus] = useState(initialAssignmentStatus)

  // Use local state for current assignment status, falling back to data if local state is null
  const assignmentStatus = localAssignmentStatus ?? initialAssignmentStatus

  // Use the systems monitoring workflow hook
  const workflow = useSystemsMonitoringWorkflow({
    systemId: data.systemId,
    instanceId: data.assignment?.monitoringInstanceId,
    assetId: data.asset?.id || data.id,
    assignmentId: data.assignment?.id,
    installationId: data.installationId,
    checkGroupConflict: data.checkGroupConflict,
    container: data.container,
    onSuccess: () => {},
    onError: () => {},
    onRefresh: data.refetch,
    entityName: "monitoring",
    pollingTimeoutMs: data.pollingTimeoutMs,
    pollingIntervalMs: data.pollingIntervalMs,
  })

  // Use node-type specific icon if available, otherwise fall back to asset type icon
  const assetIcon = data.icon || (data.nodeType && NODE_TYPE_ICONS[data.nodeType]) || DEFAULT_ASSET_ICONS[data.type] || FaCircleQuestion

  // Sync local state when data prop changes
  useEffect(() => {
    const newAssignmentStatus = data.assignment ? data.assignment.status : data.assignmentStatus
    if (newAssignmentStatus !== undefined && newAssignmentStatus !== null) {
      setLocalAssignmentStatus(newAssignmentStatus)
    }
  }, [data.assignment?.status, data.assignmentStatus, data.id])

  // Handle toggle monitoring status
  const handleToggleStatus = () => {
    if (!data.assignment) {
      alert("No assignment found for this asset")
      return
    }

    // Determine action based on current status
    const action = checked ? "deactivate" : "activate"

    // Use the workflow actions
    if (action === "activate") {
      workflow.openActivation()
    } else {
      workflow.openDeactivation()
    }
  }

  const checked = assignmentStatus === "active"

  const ApiAssetImpl = data.version === SystemNodeVersion.V1 ? ApiAssetImplV1 : ApiAssetImplV2

  return (
    data && (
      <Box
        data-testid={`node-${data.id}`}
        className={`apiAsset ${data.type?.toLowerCase() ?? ""}`}
        style={{
          userSelect: "none",
        }}
      >
        {/* Asset Implementation Component */}
        <ApiAssetImpl
          name={data.label}
          icon={assetIcon}
          assignment={data.assignment || undefined}
          attributes={data.attributes}
          statusToggle={handleToggleStatus}
          assignmentStatus={assignmentStatus}
          operationalStatus={data.operationalStatus}
          workflowState={workflow.isOpen ? "processing" : "idle"}
          disabled={data.disabled || workflow.isActivating || workflow.isDeactivating}
          controllable={data.controllable}
          asset={data.asset || undefined}
          installation={data.installationId || undefined}
          alerts={data.alerts}
          loadPrecedence={data.loadPrecedence}
        />

        {/* Handles on all four sides — rendered after content for z-index stacking */}
        <Handle id="top" type="source" position={Position.Top} isConnectable={isConnectable} className={data.connectedHandleIds?.has("top") ? "connected" : undefined} />
        <Handle id="right" type="source" position={Position.Right} isConnectable={isConnectable} className={data.connectedHandleIds?.has("right") ? "connected" : undefined} />
        <Handle id="bottom" type="source" position={Position.Bottom} isConnectable={isConnectable} className={data.connectedHandleIds?.has("bottom") ? "connected" : undefined} />
        <Handle id="left" type="source" position={Position.Left} isConnectable={isConnectable} className={data.connectedHandleIds?.has("left") ? "connected" : undefined} />

        {/* Monitoring Workflow */}
        <MonitoringActivationWorkflow
          open={workflow.isOpen}
          action={workflow.action || undefined}
          assetKind={(data.asset?.kind as GraphQLAssetKind) || null}
          assetId={data.asset?.id || data.id || null}
          defaultVolume={data.asset?.volume || null}
          defaultMinPressure={data.asset?.min_pressure || null}
          onClose={workflow.close}
          onSuccess={workflow.handleRefresh}
          onError={noop}
          onRefresh={workflow.handleRefresh}
          onActivate={workflow.handleActivate}
          onDeactivate={workflow.handleDeactivate}
          onPreActivate={workflow.handlePreActivate}
          onConflict={workflow.handleConflict}
          {...workflow.workflowProps}
          customTitles={{
            confirming: workflow.workflowProps?.confirmActivation
              ? workflow.action === "activate"
                ? "Activate monitoring"
                : "Deactivate monitoring"
              : "Deactivate monitoring",
            conflict: "Monitoring Conflict",
          }}
        />
      </Box>
    )
  )
}

export default ApiAssetNode
